/**
 * Route prefetching.
 *
 * Every page's code lives in its own chunk, so the first visit to a route shows
 * the Suspense placeholder while the chunk downloads. Loading the chunk in the
 * background — on hover of a link, or while the browser is idle — makes that
 * navigation instant instead.
 *
 * Keys are the route paths as defined in App.jsx / createPageUrl (lowercase
 * kebab-case). Add an entry when a new route is added to App.jsx.
 */
import { isActivityDetailPath } from '@/lib/activityUrl';

const routeLoaders = {
  '/ausflugsideen': () => import('@/pages/Ausflugsideen'),
  '/magazin': () => import('@/pages/Magazin'),
  '/rabatte': () => import('@/pages/Rabatte'),
  '/landingpage': () => import('@/pages/Landingpage'),
  '/favoriten': () => import('@/pages/Favorites'),
  '/meine-touren': () => import('@/pages/MyTours'),
  '/tour-detail': () => import('@/pages/TourDetail'),
  '/activities-list': () => import('@/pages/ActivitiesList'),
  '/partner-werden': () => import('@/pages/PartnerWerden'),
  '/aktivitaet-eintragen': () => import('@/pages/AktivitaetEintragen'),
  '/smart-filter': () => import('@/pages/SmartFilter'),
  '/updates': () => import('@/pages/Updates'),
  '/funktionen': () => import('@/pages/Features'),
  '/ueber-uns': () => import('@/pages/About'),
  '/haeufige-fragen': () => import('@/pages/HaeufigeFragen'),
  '/feedback': () => import('@/pages/Feedback'),
  '/sponsoring': () => import('@/pages/Sponsoring'),
  '/affiliate-links': () => import('@/pages/AffiliateLinks'),
  '/cookies': () => import('@/pages/Cookies'),
  '/datenschutz': () => import('@/pages/Privacy'),
  '/impressum': () => import('@/pages/Imprint'),
};

/** Routes warmed up in the background once the browser is idle. */
export const idlePrefetchRoutes = [
  '/ausflugsideen',
  '/landingpage',
  '/magazin',
];

const requested = new Set();

const normalize = (path) =>
  '/' + String(path).split('?')[0].split('#')[0].replace(/^\/+/, '').toLowerCase();

/** Activity detail pages live at /<category-slug>/<activity-slug>. */
function loaderFor(key) {
  const loader = routeLoaders[key];
  if (loader) return loader;
  if (isActivityDetailPath(key)) return () => import('@/pages/ActivityDetail');
  return null;
}

/** Loads the code chunk for a route path, at most once per session. */
export function prefetchRoute(path) {
  // Warming up a route competes for bandwidth with the page the user is on, so
  // it is skipped when the connection can ill afford it.
  if (typeof navigator !== 'undefined' && (navigator.onLine === false || navigator.connection?.saveData)) return;
  const key = normalize(path);
  const loader = loaderFor(key);
  if (!loader || requested.has(key)) return;
  requested.add(key);
  // A failed prefetch is not user-visible: the route loads normally on click.
  loader().catch(() => requested.delete(key));
}